'use strict';

import { $ } from './ApiHelper.js';
import { getPendingImages, removeQueuedImage } from './ImageQueueHelper.js';
import { getPendingSaves, clearPendingSave } from './PendingSaveHelper.js';

export const login = (email, password) => {
  return new Promise((resolve, reject) => {
    $({
      authRequest: true,
      url: `/token`,
      type: `POST`,
      body: {
        email, 
        password
      },
      success: resolve,
      error: reject
    });
  });
};

export const getMe = () => {
  return new Promise((resolve, reject) => {
    $({
      url: `/me`,
      success: resolve,
      error: reject
    });
  });
};

export const getProjectGroups = () => {
  return new Promise((resolve, reject) => {
    $({
      url: `/project_groups`,
      success: resolve,
      error: reject
    });
  });
};

export const refreshToken = (refresh) => {
  return new Promise((resolve, reject) => {
    $({
      authRequest: true,
      url: `/token/refresh`,
      type: `POST`,
      body: {
        refresh
      },
      success: resolve,
      error: reject
    });
  });
};

export const getProjects = (page, installer, search, status) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/projects`,
      params: {
        page,
        search,
        installer,
        status
      },
      success: resolve,
      error: reject
    });
  });
};

export const getProject = (id) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/projects/${ id }`,
      success: resolve,
      error: reject
    });
  });
};

export const updateProject = (id, body) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/projects/${ id }`,
      type: `PATCH`,
      body,
      success: resolve,
      error: reject
    });
  });
};

export const getTask = (id) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/tasks/${ id }`,
      success: resolve,
      error: reject
    });
  });
};

export const updateTask = (id, task) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/tasks/${ id }`,
      type: `PATCH`,
      body: task,
      success: resolve,
      error: reject
    });
  });
};

export const createTask = (task) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/tasks`,
      type: `POST`,
      body: task,
      success: resolve,
      error: reject
    });
  });
};

export const getImage = (pk) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/images`,
      params: {
        pk
      },
      success: ({ results }) => resolve(results[0] || null),
      error: reject
    });
  });
};

export const getImages = (pk_array) => {
  return Promise.all(pk_array.map(getImage));
};

export const uploadImage = (file) => {
  return new Promise((resolve, reject) => {
    let data = new FormData();

    data.append(`file`, file);

    $({
      url: `/files`,
      type: `POST`,
      api: `root`,
      formData: data,
      success: resolve,
      error: reject
    });
  });
};

export const updateTaskItem = (id, task_id, values) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/task_items/${ id }`,
      type: `PATCH`,
      body: {
        task: task_id,
        values
      },
      success: resolve,
      error: reject
    });
  });
};

export const updateTaskItemName = (id, name) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/task_items/${ id }`,
      type: `PATCH`,
      body: {
        name
      },
      success: resolve,
      error: reject
    });
  });
};

export const createTaskItem = (task_id, item, values) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/tasks/${ task_id }/task_items`,
      type: `POST`,
      body: {
        ...item,
        values
      },
      success: resolve,
      error: reject
    });
  });
};

export const getProjectAttachmentData = (attachment_id) => {
  return new Promise((resolve, reject) => {
    $({
      url: `/project_data/${ attachment_id }`,
      success: resolve,
      error: reject
    });
  });
};

let imageQueueRunning = false;

export const processImageQueue = async () => {
  if (imageQueueRunning) return;
  imageQueueRunning = true;
  try {
    let pending;
    try {
      pending = await getPendingImages();
    } catch (e) {
      return;
    }

    for (const entry of pending) {
      try {
        const uploadResponse = await uploadImage(entry.file);
        const pk = uploadResponse.id;

        const task = await getTask(entry.taskId);
        const taskItem = (task.task_items || []).find((i) => i.pk === entry.taskItemId);
        if (!taskItem) {
          await removeQueuedImage(entry.id);
          continue;
        }

        const existingField = (taskItem.values || []).find((v) => v.field === entry.fieldId);
        const existingPks = existingField ? existingField.value.split(`,`).filter(Boolean) : [];
        const newValue = [...existingPks, pk].join(`,`);

        await updateTaskItem(entry.taskItemId, entry.taskId, [{ field: entry.fieldId, value: newValue }]);
        await removeQueuedImage(entry.id);
      } catch (e) {
        // leave in queue, retry on next reconnect
      }
    }
  } finally {
    imageQueueRunning = false;
  }
};

let pendingSavesRunning = false;

export const processPendingSaves = async () => {
  if (pendingSavesRunning) return;
  pendingSavesRunning = true;
  try {
    const pending = getPendingSaves();
    for (const save of pending) {
      try {
        await updateTaskItem(save.taskItemId, save.taskId, save.fields);
        clearPendingSave(save.taskItemId);
        window.dispatchEvent(new CustomEvent(`plaid:save-synced`, { detail: { taskItemId: save.taskItemId } }));
      } catch (e) {
        // leave in queue, retry on next reconnect
      }
    }
  } finally {
    pendingSavesRunning = false;
  }
};



// WEBPACK FOOTER //
// ./src/helpers/AppDataHelper.js